import { useTheme } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Check,
  Close,
  Document,
  Text,
  Toggle,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useSearchGroups } from "@/api/graphql/ledgers/searchGroups/hooks";
import {
  LedgersMsSearchGroupsResponseData,
  MiddleMsUpdateFormDataRuleGroupRequestType,
} from "@/api/graphql/types/graphql";
import {
  StyledEditorColumn,
  StyledFieldsColumn,
  StyledFormContainer,
} from "@/components/formDataInjector/EditForm/styles";
import { EditFormProps } from "@/components/formDataInjector/EditForm/types";
import { useHandleFormDataRuleGroup } from "@/components/formDataInjector/hooks";
import { StyledText } from "@/components/formDataInjector/RulesDrawer/styles";
import {
  StyledButtonsContainer,
  StyledForm,
  StyledToggleContainer,
} from "@/components/formDataInjector/styles";
import {
  HandleFormDataRuleGroupType,
  ruleFormSchema,
  RuleFormType,
} from "@/components/formDataInjector/types";
import {
  formatStringToArray,
  fromArrayToConcatenatedString,
} from "@/components/formDataInjector/utils";
import { ControlledSelect } from "@/components/shared/ControlledSelect";
import { ControlledTextField } from "@/components/shared/ControlledTextField";
import { YamlEditor } from "@/components/shared/YamlEditor";
import { useAddSnackbar } from "@/contexts/snackbar";

export const EditForm = ({ value, resetState }: EditFormProps) => {
  const { t: tFormDataInjector } = useTranslation("formDataInjector");
  const [disableDuplicate, setDisableDuplicate] = useState(false);
  const addSnackbar = useAddSnackbar();
  const { data: groups, isLoading: isLoadingGroups } = useSearchGroups();
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RuleFormType>({
    resolver: zodResolver(ruleFormSchema),
    values: {
      name: value.name,
      description: value.description,
      rules: value.rules,
      id: value.id,
      enabled: value.enabled,
      enabledFor: fromArrayToConcatenatedString(value.enabledFor),
      disabledFor: fromArrayToConcatenatedString(value.disabledFor),
      codes: fromArrayToConcatenatedString(value.codes),
      numberOfRules: value.numberOfRules,
      groupCodeFor: value.groupCodeFor || [],
      type:
        value.type &&
        value.type !==
          MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeUnknown
          ? (value.type as
              | MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCopy
              | MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCreation
              | MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCreationOrModification
              | MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeModification)
          : undefined,
    },
  });

  const { handleClick, handleSubmit: handleSubmitFormDataInjector } =
    useHandleFormDataRuleGroup({
      resetState,
      addSnackbar,
      translateFunction: tFormDataInjector,
    });

  const theme = useTheme();

  useEffect(() => {
    if (disableDuplicate) {
      setTimeout(() => {
        setDisableDuplicate(false);
      }, 3000);
    }
  }, [disableDuplicate]);

  const onSubmit = (values: RuleFormType) => {
    const { groupCodeFor, ...rest } = values;
    handleSubmitFormDataInjector(HandleFormDataRuleGroupType.UPDATE, {
      ...rest,
      enabledFor: rest.enabledFor
        ? formatStringToArray(rest.enabledFor)
        : undefined,
      disabledFor: rest.disabledFor
        ? formatStringToArray(rest.disabledFor)
        : undefined,
      codes: rest.codes ? formatStringToArray(rest.codes) : undefined,
      groupCodeFor:
        groupCodeFor && groupCodeFor.length > 0 ? groupCodeFor : undefined,
    });
  };

  const isButtonDisabled = Object.keys(errors).length > 0;

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <StyledFormContainer>
        <StyledEditorColumn>
          <Controller
            name='rules'
            control={control}
            render={({ field: { value, onChange } }) => (
              <YamlEditor
                value={value ?? ""}
                onChange={onChange}
                setError={(message) => {
                  if (message) {
                    setError("rules", { type: "manual", message });
                  } else {
                    clearErrors("rules");
                  }
                }}
                height='400px'
              />
            )}
          />
        </StyledEditorColumn>

        <StyledFieldsColumn>
          <StyledToggleContainer>
            <Text variant='title1'>
              {tFormDataInjector("formDataInjector.inputs.activation")}
            </Text>
            <Controller
              name='enabled'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle
                  isActive={!!value}
                  onChange={onChange}
                  data-testid='formdata-injector__toggle-button'
                />
              )}
            />
          </StyledToggleContainer>

          <ControlledTextField
            name='name'
            control={control}
            namespace='formDataInjector'
            textFieldProps={{
              label: tFormDataInjector("formDataInjector.inputs.name"),
              dataTestId: "formdata-injector__rule-name-input",
            }}
            data-testid='formdata-injector__rule-name-input'
          />

          <ControlledTextField
            name='description'
            control={control}
            namespace='formDataInjector'
            textFieldProps={{
              label: tFormDataInjector("formDataInjector.inputs.description"),
              dataTestId: "formdata-injector__rule-description-input",
            }}
            data-testid='formdata-injector__rule-description-input'
          />

          <ControlledSelect
            name='type'
            control={control}
            namespace='formDataInjector'
            selectProps={{
              label: tFormDataInjector("formDataInjector.forms.type"),
              dataTestId: "formdata-injector__form-rule-type",
              type: "options",
              options: [
                {
                  label: tFormDataInjector("formDataInjector.forms.creation"),
                  value:
                    MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCreation,
                },
                {
                  label: tFormDataInjector(
                    "formDataInjector.forms.modification",
                  ),
                  value:
                    MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeModification,
                },
                {
                  label: tFormDataInjector(
                    "formDataInjector.forms.createOrEdit",
                  ),
                  value:
                    MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCreationOrModification,
                },
                {
                  label: tFormDataInjector("formDataInjector.forms.copy"),
                  value:
                    MiddleMsUpdateFormDataRuleGroupRequestType.FormRuleTypeCopy,
                },
              ],
            }}
          />

          <ControlledSelect
            name='groupCodeFor'
            control={control}
            namespace='formDataInjector'
            selectProps={{
              label: tFormDataInjector("formDataInjector.forms.ledgerGroup"),
              dataTestId: "formdata-injector__form-rule-ledger-group",
              type: "options",
              multiple: true,
              disabled: isLoadingGroups,
              options: groups.map(
                (group: LedgersMsSearchGroupsResponseData) => ({
                  label: group.codeHint,
                  value: group.code,
                }),
              ),
            }}
          />
          <div>
            <ControlledTextField
              name='enabledFor'
              control={control}
              namespace='formDataInjector'
              textFieldProps={{
                label: tFormDataInjector("formDataInjector.forms.enabledFor"),
                dataTestId: "formdata-injector__rule-enabledfor-input",
              }}
              data-testid='formdata-injector__rule-enabledfor-input'
            />
            <StyledText variant='sub2' fontWeight='light' fontStyle='italic'>
              {tFormDataInjector("formDataInjector.forms.enabledFor.helper")}
            </StyledText>
          </div>
          <div>
            <ControlledTextField
              name='disabledFor'
              control={control}
              namespace='formDataInjector'
              textFieldProps={{
                label: tFormDataInjector("formDataInjector.forms.disabledFor"),
                dataTestId: "formdata-injector__rule-disabledfor-input",
              }}
              data-testid='formdata-injector__rule-disabledfor-input'
            />
            <StyledText variant='sub2' fontWeight='light' fontStyle='italic'>
              {tFormDataInjector("formDataInjector.forms.disabledFor.helper")}
            </StyledText>
          </div>
          <div>
            <ControlledTextField
              name='codes'
              control={control}
              namespace='formDataInjector'
              textFieldProps={{
                label: tFormDataInjector("formDataInjector.forms.codes"),
                dataTestId: "formdata-injector__rule-codes-input",
              }}
              data-testid='formdata-injector__rule-codes-input'
            />
            <StyledText variant='sub2' fontWeight='light' fontStyle='italic'>
              {tFormDataInjector("formDataInjector.forms.codes.helper")}
            </StyledText>
          </div>
        </StyledFieldsColumn>
      </StyledFormContainer>

      <StyledButtonsContainer>
        <Button
          type='submit'
          disabled={isButtonDisabled}
          leftIcon={
            <Check width={24} height={24} color={theme.palette.white} />
          }
          onClick={undefined}
          buttonText={tFormDataInjector("formDataInjector.buttons.save")}
          variant='primary'
          size='m'
          dataTestId='formdata-injector__save-button'
        />
        <Button
          onClick={() => {
            handleClick(HandleFormDataRuleGroupType.DUPLICATE, value.id ?? "");
            setDisableDuplicate(true);
          }}
          leftIcon={
            <Document width={24} height={24} color={theme.palette.white} />
          }
          buttonText={tFormDataInjector("formDataInjector.buttons.clone")}
          variant='primary'
          size='m'
          disabled={disableDuplicate}
          dataTestId='formdata-injector__clone-button'
        />
        <Button
          leftIcon={
            <Close width={18} height={18} color={theme.palette.white} />
          }
          onClick={() => {
            handleClick(HandleFormDataRuleGroupType.DELETE, value.id ?? "");
          }}
          buttonText={tFormDataInjector("formDataInjector.buttons.delete")}
          variant='primary'
          colorScheme='emergency'
          size='m'
          dataTestId='formdata-injector__delete-button'
        />
      </StyledButtonsContainer>
    </StyledForm>
  );
};
